import React, { useState, useEffect } from 'react';
import { X, Server, CheckCircle2, AlertCircle, RotateCcw } from 'lucide-react';
import { getApiUrl, setApiUrl, resetApiUrl } from '../../services/logsApi';

interface HostSettingsModalProps {
  open: boolean;
  onClose: () => void;
}

const t = {
  primary: '#7c3aed',
  success: '#16a34a',
  successBg: '#dcfce7',
  danger: '#ef4444',
  dangerBg: 'rgba(239,68,68,0.08)',
  surface: '#ffffff',
  border: '#e9e5f5',
  overlay: 'rgba(30,27,75,0.55)',
  bg: '#f5f3ff',
  text: { primary: '#1e1b4b', secondary: '#6b7280', muted: '#9ca3b8' },
};

type TestStatus = 'idle' | 'testing' | 'ok' | 'error';

export default function HostSettingsModal({ open, onClose }: HostSettingsModalProps) {
  const [value, setValue] = useState('');
  const [testStatus, setTestStatus] = useState<TestStatus>('idle');
  const [testMessage, setTestMessage] = useState('');

  useEffect(() => {
    if (open) {
      setValue(getApiUrl());
      setTestStatus('idle');
      setTestMessage('');
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const testConnection = async () => {
    setTestStatus('testing');
    setTestMessage('');
    try {
      const url = value.replace(/\/+$/, '');
      const res = await fetch(`${url}/api/health`, { signal: AbortSignal.timeout(5000) });
      const data = await res.json();
      if (res.ok && data.status === 'OK') {
        setTestStatus('ok');
        setTestMessage(`Connecté — Elasticsearch : ${data.elasticsearch}`);
      } else {
        setTestStatus('error');
        setTestMessage(data.error || `Réponse inattendue (HTTP ${res.status})`);
      }
    } catch (err: any) {
      setTestStatus('error');
      setTestMessage(err.message || 'Impossible de joindre le serveur');
    }
  };

  const handleSave = () => {
    setApiUrl(value);
    onClose();
    window.location.reload();
  };

  const handleReset = () => {
    resetApiUrl();
    setValue(getApiUrl());
    setTestStatus('idle');
    setTestMessage('');
  };

  return (
    <div
      className="fixed inset-0 z-[1000] flex items-center justify-center p-4"
      style={{ backgroundColor: t.overlay, backdropFilter: 'blur(6px)' }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        className="relative w-full max-w-md rounded-2xl p-6 shadow-2xl"
        style={{ backgroundColor: t.surface, border: `1px solid ${t.border}`, animation: 'modalIn 200ms cubic-bezier(0.34,1.56,0.64,1) both' }}
      >
        {/* Close */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-1.5 rounded-lg transition-colors"
          style={{ color: t.text.muted, background: 'transparent', border: 'none', cursor: 'pointer' }}
        >
          <X className="w-4 h-4" />
        </button>

        {/* Header */}
        <div className="flex items-center gap-3 mb-5">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: '#ede9fe' }}>
            <Server className="w-5 h-5" style={{ color: t.primary }} />
          </div>
          <div>
            <h2 className="text-[16px] font-bold" style={{ color: t.text.primary }}>Serveur API</h2>
            <p className="text-[12px]" style={{ color: t.text.muted }}>URL de base du backend</p>
          </div>
        </div>

        {/* Input */}
        <label className="block text-[12px] font-semibold mb-1.5" style={{ color: t.text.secondary }}>
          Host
        </label>
        <input
          type="url"
          value={value}
          onChange={(e) => { setValue(e.target.value); setTestStatus('idle'); setTestMessage(''); }}
          placeholder="https://example.com"
          className="w-full h-10 px-3 rounded-lg text-[13px] font-mono outline-none mb-3"
          style={{ border: `1px solid ${t.border}`, backgroundColor: t.bg, color: t.text.primary }}
          spellCheck={false}
        />

        {/* Test feedback */}
        {testStatus !== 'idle' && (
          <div
            className="flex items-center gap-2 px-3 py-2 rounded-lg mb-3 text-[12px]"
            style={{
              backgroundColor: testStatus === 'ok' ? t.successBg : testStatus === 'error' ? t.dangerBg : '#f5f3ff',
              color: testStatus === 'ok' ? t.success : testStatus === 'error' ? t.danger : t.primary,
            }}
          >
            {testStatus === 'testing' && <span className="w-3.5 h-3.5 rounded-full border-2 border-current border-t-transparent animate-spin flex-shrink-0" />}
            {testStatus === 'ok' && <CheckCircle2 className="w-3.5 h-3.5 flex-shrink-0" />}
            {testStatus === 'error' && <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />}
            <span>{testStatus === 'testing' ? 'Test en cours…' : testMessage}</span>
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center gap-2">
          <button
            onClick={handleReset}
            title="Remettre l'URL par défaut"
            className="p-2 rounded-lg transition-colors"
            style={{ border: `1px solid ${t.border}`, background: 'transparent', color: t.text.muted, cursor: 'pointer' }}
          >
            <RotateCcw className="w-3.5 h-3.5" />
          </button>
          <button
            onClick={testConnection}
            disabled={testStatus === 'testing' || !value.trim()}
            className="flex-1 py-2 rounded-xl text-[13px] font-medium transition-all"
            style={{ border: `1px solid ${t.primary}`, background: 'transparent', color: t.primary, cursor: 'pointer', opacity: testStatus === 'testing' ? 0.6 : 1 }}
          >
            Tester
          </button>
          <button
            onClick={handleSave}
            disabled={!value.trim()}
            className="flex-1 py-2 rounded-xl text-[13px] font-semibold transition-all"
            style={{ background: t.primary, color: '#fff', border: 'none', cursor: 'pointer', opacity: !value.trim() ? 0.5 : 1 }}
          >
            Sauvegarder
          </button>
        </div>
      </div>

      <style>{`
        @keyframes modalIn {
          from { opacity: 0; transform: scale(0.92) translateY(8px); }
          to   { opacity: 1; transform: scale(1) translateY(0); }
        }
      `}</style>
    </div>
  );
}
